// src/services/authService.js
import apiClient from './api';

export const authService = {
  login: async (username, password) => {
    try {
      console.log("Sending login request with username:", username);
      const response = await apiClient.post('/users/login/', { username, password });
      console.log("Raw login response:", response.data);

      if (response.status >= 400 || response.data?.error) {
        return {
          success: false,
          error: response.data?.error || 'Invalid credentials'
        };
      }

      // Pre-warm permission cache for faster first-time access
      if (response.data?.user) {
        authService.warmPermissionCache(response.data.user).catch(err =>
          console.warn('[AuthService] Permission cache warming failed:', err)
        );
      }

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error("Login API error:", error.response?.data || error.message);
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  },

  // Pre-warm commonly accessed permissions and data after login
  warmPermissionCache: async (user) => {
    if (!user) return;
    
    try {
      console.log('[AuthService] Warming permission cache for user:', user.username);
      
      const warmingPromises = [
        // Always warm these regardless of user type
        apiClient.get('/permissions/dashboard-cards/').catch(() => null),
        apiClient.get('/permissions/routes/').catch(() => null),
      ];
      
      if (user.is_staff || user.is_superuser) {
        // For admin users, pre-warm common admin resources
        warmingPromises.push(
          apiClient.get('/customers/', { params: { page_size: 10 } }).catch(() => null),
          apiClient.get('/products/categories/').catch(() => null),
          apiClient.get('/products/', { params: { page_size: 10 } }).catch(() => null)
        );
      } else if (user.customer_profile) {
        // For customer users, pre-warm their specific data
        warmingPromises.push(
          apiClient.get('/customers/me/').catch(() => null),
          apiClient.get('/products/categories/').catch(() => null)
        );
      }
      
      // Execute all warming requests in parallel without blocking login
      const results = await Promise.allSettled(warmingPromises);
      const successful = results.filter(r => r.status === 'fulfilled').length;
      console.log(`[AuthService] Permission cache warming completed: ${successful}/${results.length} successful`);
      
    } catch (error) {
      console.warn('[AuthService] Permission cache warming error:', error);
    }
  },
  
  logout: async (refreshToken) => {
    try {
      // Always clear tokens first (in case of network issues)
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      
      // Attempt to notify server, but don't fail if token is expired
      if (refreshToken) {
        try {
          await apiClient.post('/users/logout/', { refresh: refreshToken });
        } catch (logoutError) {
          // If logout fails due to expired token (401), that's fine
          if (logoutError.response?.status !== 401) {
            console.warn('Server logout notification failed:', logoutError);
          }
        }
      }
      
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      // Always clear tokens even if logout fails
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      return { success: true }; // Changed to true since tokens are cleared
    }
  },
  
  getUserProfile: async () => {
    try {
      const response = await apiClient.get('/users/');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get user profile'
      };
    }
  },
  
  updateProfile: async (userData) => {
    try {
      const response = await apiClient.put('/customers/update/', userData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update profile'
      };
    }
  },

  switchUser: (authData) => {
    try {
      localStorage.setItem('access_token', authData.access_token);
      localStorage.setItem('refresh_token', authData.refresh_token);
      localStorage.setItem('user_data', JSON.stringify(authData.user));

      if (authData.user) {
        authService.warmPermissionCache(authData.user).catch(err =>
          console.warn('[AuthService] Permission cache warming failed:', err)
        );
      }

      return {
        success: true,
        data: authData
      };
    } catch (error) {
      console.error('Switch user error:', error);
      return {
        success: false,
        error: 'Failed to switch user'
      };
    }
  }
};